import React, { Component } from "react";
import Player from "./Player";

export default class PlayListComponent extends Component {
  constructor(props) {
    super(props);
    this.audioRef = React.createRef();
    this.state = {
      currentTrackId: null,
      isPlaying: false,
      progress: 0,
      duration: 0,
    };
  }

  componentDidUpdate(prevProps) {
    // If the album changes, reset the player
    if (prevProps.media !== this.props.media) {
      this.stopPlayback();
    }
  }

  componentWillUnmount() {
    this.stopPlayback();
  }

  stopPlayback = () => {
    const audio = this.audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    this.setState({
      currentTrackId: null,
      isPlaying: false,
      progress: 0,
      duration: 0,
    });
  };

  togglePlay = (track) => {
    const audio = this.audioRef.current;
    if (!audio) return;

    // If clicking the currently playing track
    if (this.state.currentTrackId === track.id) {
      if (this.state.isPlaying) {
        audio.pause();
        this.setState({ isPlaying: false });
      } else {
        audio.play();
        this.setState({ isPlaying: true });
      }
      return;
    }

    // New track
    audio.src = track.file;
    audio.load();
    audio.play();
    this.setState({
      currentTrackId: track.id,
      isPlaying: true,
      progress: 0,
    });
  };

  handleTimeUpdate = () => {
    const audio = this.audioRef.current;
    if (!audio || !audio.duration) return;
    this.setState({
      progress: (audio.currentTime / audio.duration) * 100,
    });
  };

  handleLoadedMetadata = () => {
    const audio = this.audioRef.current;
    if (!audio) return;
    this.setState({ duration: audio.duration });
  };

  handleSeek = (e, track) => {
    if (this.state.currentTrackId !== track.id) return;
    const audio = this.audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const pct = clickX / rect.width;
    audio.currentTime = pct * audio.duration;
    this.setState({ progress: pct * 100 });
  };

  formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // Trigger a browser download for a given track's file URL
  handleDownload = (track) => {
    if (!track.file) return;
    const link = document.createElement("a");
    link.href = track.file;
    link.download = `${track.title}.mp3`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { currentTrackId, isPlaying, progress, duration } = this.state;
    const media = this.props.media || [];

    return (
      <div className="fsm-tracklist">
        {/* Hidden audio element used for playback */}
        <audio
          ref={this.audioRef}
          onTimeUpdate={this.handleTimeUpdate}
          onLoadedMetadata={this.handleLoadedMetadata}
          onEnded={() =>
            this.setState({ isPlaying: false, progress: 0 })
          }
        />

        {media.map((track, i) => {
          const isAudio = track.media_type === "Audio";
          const isCurrent = currentTrackId === track.id;
          const pct = isCurrent ? progress : 0;

          // Non-audio tracks (videos) keep the old Player
          if (!isAudio) {
            return (
              <div className="fsm-track-video" key={track.id || i}>
                <Player
                  url={track.link}
                  controls
                  light={true}
                  width={"100%"}
                />
              </div>
            );
          }

          return (
            <div
              className={`fsm-track-row ${isCurrent ? "fsm-track-current" : ""}`}
              key={track.id || i}
            >
              <div className="fsm-track-index">
                {String(i + 1).padStart(2, "0")}
              </div>

              <button
                type="button"
                className="fsm-track-play"
                onClick={() => this.togglePlay(track)}
                aria-label={isCurrent && isPlaying ? "Pause" : "Play"}
              >
                {isCurrent && isPlaying ? (
                  <i className="fa fa-pause"></i>
                ) : (
                  <i className="fa fa-play"></i>
                )}
              </button>

              <div className="fsm-track-info">
                <div className="fsm-track-title">{track.title}</div>
                <div className="fsm-track-artist">
                  {track.artist || "FSM Team"}
                </div>

                {isCurrent && (
                  <div
                    className="fsm-track-progress"
                    onClick={(e) => this.handleSeek(e, track)}
                  >
                    <div
                      className="fsm-track-progress-fill"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </div>

              {isCurrent && (
                <div className="fsm-track-time">
                  {this.formatTime((progress / 100) * duration)}
                  {" / "}
                  {this.formatTime(duration)}
                </div>
              )}

              <button
                type="button"
                className="fsm-track-download"
                onClick={() => this.handleDownload(track)}
                aria-label={`Download ${track.title}`}
                title="Download"
              >
                <i className="fa fa-download"></i>
              </button>
            </div>
          );
        })}
      </div>
    );
  }
}